import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Lock, Shield } from 'lucide-react';
import { ROUTES, VALIDATION_MESSAGES } from '@constants';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import PhoneInput from '@components/common/PhoneInput';
import PasswordInput from '@components/common/PasswordInput';
import toast from 'react-hot-toast';
import authService from '@services/auth.service';

const phoneSchema = z.object({
  phone: z.string()
    .min(10, 'Phone number must be at least 10 digits')
    .max(15, 'Phone number must not exceed 15 digits')
    .regex(/^[0-9]+$/, 'Phone number must contain only digits'),
});

const otpSchema = z.object({
  otp: z.string()
    .length(6, 'OTP must be 6 digits')
    .regex(/^[0-9]+$/, 'OTP must contain only digits'),
});

const passwordSchema = z.object({
  newPassword: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [userId, setUserId] = useState(null);
  const [resetToken, setResetToken] = useState(null);
  const [maskedPhone, setMaskedPhone] = useState('');

  // Step 1: Phone form
  const phoneForm = useForm({
    resolver: zodResolver(phoneSchema),
  });

  // Step 2: OTP form
  const otpForm = useForm({
    resolver: zodResolver(otpSchema),
  });

  // Step 3: Password form
  const passwordForm = useForm({
    resolver: zodResolver(passwordSchema),
  });

  const newPasswordValue = passwordForm.watch('newPassword');

  const onPhoneSubmit = async (data) => {
    try {
      const response = await authService.forgotPassword(data.phone);
      if (response.success) {
        // API service wraps response: response.data contains backend response
        const backendData = response.data.data || response.data;
        setUserId(backendData.userId);
        setMaskedPhone(backendData.phone);
        setStep(2);
        toast.success(response.data.message || 'OTP sent to your phone');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to send OTP');
    }
  };

  const onOTPSubmit = async (data) => {
    try {
      const response = await authService.verifyForgotPasswordOTP(userId, data.otp);
      if (response.success) {
        // API service wraps response: response.data contains backend response
        const backendData = response.data.data || response.data;
        setResetToken(backendData.resetToken);
        setStep(3);
        toast.success(response.data.message || 'OTP verified successfully');
      }
    } catch (error) {
      toast.error(error.message || 'Invalid OTP');
    }
  };

  const onPasswordSubmit = async (data) => {
    try {
      const response = await authService.resetPassword(resetToken, data.newPassword);
      if (response.success) {
        toast.success(response.data.message || 'Password reset successful');
        setTimeout(() => navigate(ROUTES.LOGIN), 2000);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to reset password');
    }
  };

  return (
    <div>
      {/* Step Indicator */}
      <div className="flex justify-center mb-8">
        <div className="flex items-center gap-2">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
            step >= 1 ? 'bg-secondary-500 text-white' : 'bg-gray-300 text-gray-600'
          }`}>
            <Phone className="w-4 h-4" />
          </div>
          <div className={`w-12 h-1 ${
            step >= 2 ? 'bg-secondary-500' : 'bg-gray-300'
          }`} />
          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
            step >= 2 ? 'bg-secondary-500 text-white' : 'bg-gray-300 text-gray-600'
          }`}>
            <Shield className="w-4 h-4" />
          </div>
          <div className={`w-12 h-1 ${
            step >= 3 ? 'bg-secondary-500' : 'bg-gray-300'
          }`} />
          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
            step >= 3 ? 'bg-secondary-500 text-white' : 'bg-gray-300 text-gray-600'
          }`}>
            <Lock className="w-4 h-4" />
          </div>
        </div>
      </div>

      {/* Step 1: Phone Number */}
      {step === 1 && (
        <div>
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-primary-700">Forgot Password</h2>
            <p className="text-primary-600 mt-2">
              Enter your phone number to receive an OTP
            </p>
          </div>

          <form onSubmit={phoneForm.handleSubmit(onPhoneSubmit)} className="space-y-4">
            <PhoneInput
              label="Phone Number"
              placeholder="Enter your phone number (e.g., 919888892022)"
              error={phoneForm.formState.errors.phone?.message}
              helperText="Include country code (e.g., 91 for India)"
              fullWidth
              {...phoneForm.register('phone')}
            />

            <Button type="submit" variant="primary" fullWidth loading={phoneForm.formState.isSubmitting}>
              Send OTP
            </Button>
          </form>
        </div>
      )}

      {/* Step 2: OTP Verification */}
      {step === 2 && (
        <div>
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-primary-700">Verify OTP</h2>
            <p className="text-primary-600 mt-2">
              Enter the 6-digit OTP sent to {maskedPhone}
            </p>
          </div>

          <form onSubmit={otpForm.handleSubmit(onOTPSubmit)} className="space-y-4">
            <Input
              label="OTP"
              type="text"
              placeholder="Enter 6-digit OTP"
              maxLength={6}
              error={otpForm.formState.errors.otp?.message}
              fullWidth
              {...otpForm.register('otp')}
            />

            <Button type="submit" variant="primary" fullWidth loading={otpForm.formState.isSubmitting}>
              Verify OTP
            </Button>

            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={() => setStep(1)}
            >
              Change Phone Number
            </Button>
          </form>
        </div>
      )}

      {/* Step 3: New Password */}
      {step === 3 && (
        <div>
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-primary-700">Reset Password</h2>
            <p className="text-primary-600 mt-2">
              Enter your new password
            </p>
          </div>

          <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4">
            <PasswordInput
              label="New Password"
              placeholder="Enter new password"
              error={passwordForm.formState.errors.newPassword?.message}
              fullWidth
              showStrength
              strengthValue={newPasswordValue}
              {...passwordForm.register('newPassword')}
            />

            <PasswordInput
              label="Confirm Password"
              placeholder="Confirm new password"
              error={passwordForm.formState.errors.confirmPassword?.message}
              fullWidth
              {...passwordForm.register('confirmPassword')}
            />

            <div className="text-xs text-primary-500 space-y-1">
              <p>Password must contain:</p>
              <ul className="list-disc list-inside pl-2">
                <li>At least 8 characters</li>
                <li>One uppercase letter</li>
                <li>One lowercase letter</li>
                <li>One number</li>
              </ul>
            </div>

            <Button type="submit" variant="primary" fullWidth loading={passwordForm.formState.isSubmitting}>
              Reset Password
            </Button>
          </form>
        </div>
      )}

      <div className="mt-6 text-center">
        <Link
          to={ROUTES.LOGIN}
          className="inline-flex items-center gap-2 text-sm text-secondary-500 hover:underline"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Sign In
        </Link>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
